/// <amd-module name="Test/TestPlansLibrary/Pivots/TestCasesPivot/Redux/TestCasesSaga" />
import * as React from "react";
import { effects, SagaIterator, Channel, channel } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { TestCasesActions, TestCasesActionTypes } from "./TestCasesActions";
import { getColumnOptionFields, getWorkItemDataProviderStateFromState } from "./TestCasesSelector";
import { LibraryQueryConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";
import { TestPlansLibraryActions, TestPlansLibraryActionsTypes } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryActions";
import { ILibraryWorkItemDataProviderState } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryContracts";
import {
    createWorkItemDataProviderState,
    getDataProviderContent,
    getDataProviderContentAsync,
    getDataProviderContextContract
} from "Test/TestPlansLibrary/LibraryHub/Utils/TestPlansLibraryUtils";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { getAllPossibleFields } from "Test/TestPlansLibrary/ColumnOptions/TestCasesColumnOptionUtils";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { ContentViewObserverActions, ContentViewObserverActionTypes } from "VSS/Redux/Modules/ContentViewObserver";
import { IFilterDetails } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { ITfsPageService } from "Tfs/Platform/Page";

export function* TestCasesSaga(): SagaIterator {
    yield effects.takeLatest(TestCasesActionTypes.initializeTestCasesPivot, initializeTestCasesPivot);
    yield effects.takeLatest(TestCasesActionTypes.loadInitialTestCases, loadInitialTestCases);
    yield effects.takeLatest(TestCasesActionTypes.loadTestCasesPage, loadTestCasesPage);
    yield effects.takeLatest(TestPlansLibraryActionsTypes.editColumnOptionFieldsClicked, editColumnOptionFieldsClicked);
}

/**
 * Initial actions for test cases pivot
 */
export function* initializeTestCasesPivot(
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    if (!pageData || !pageData.project) {
        throw new Error("Project missing");
    }

    const projectId = pageData.project.id;

    let initialFilterDetails: IFilterDetails = {
        projectId,
        filterState: {},
        filterVisible: false,
        filterKey: "testPlansLibraryFilterKey"
    };

    yield effects.put(ContentViewObserverActions.initializeFilterState(initialFilterDetails));

    yield effects.put(TestCasesActions.clearTestCasesAndSetLoadingExperience());
}

/**
 * Load initial test cases for test cases tab from json island
 */
export function* loadInitialTestCases(action: ActionsOfType<TestCasesActions, TestCasesActionTypes.loadInitialTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const data = (yield effects.call(getDataProviderContent, pageContext)) as any;

    // We assume that the first query is always of all test cases
    const dataProviderState = (yield effects.call(createWorkItemDataProviderState, data, LibraryQueryConstants.QueryAllTestCasesKey)) as any;

    yield effects.put(TestCasesActions.loadInitialTestCasesSucceeded(data.workItems, dataProviderState));
}

/**
 * Saga to load a subsequent page of test cases
 */
export function* loadTestCasesPage(action: ActionsOfType<TestCasesActions, TestCasesActionTypes.loadTestCasesPage>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const providerContext: ILibraryWorkItemDataProviderState = (yield effects.select(getWorkItemDataProviderStateFromState)) as any;

    const providerContextForNextCall: Contracts.LibraryWorkItemsDataProviderRequest = (yield effects.call(
        getDataProviderContextContract,
        providerContext
    )) as any;

    const data = (yield effects.call(getDataProviderContentAsync, pageContext, providerContextForNextCall)) as any;
    const dataProviderState = (yield effects.call(createWorkItemDataProviderState, data, providerContext.queryType)) as any;

    if (data) {
        yield effects.put(TestCasesActions.loadTestCasesPageSucceeded(data.workItems, dataProviderState));
    }
}

/**
 * Saga to open column options panel to set column options
 */
export function* editColumnOptionFieldsClicked(
    action: ActionsOfType<TestPlansLibraryActions, TestPlansLibraryActionsTypes.editColumnOptionFieldsClicked>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const columnOptionFields = (yield effects.select(getColumnOptionFields)) as any;

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const allColumnFields = getAllPossibleFields();

    const updateColumnFieldsChannel: Channel<{}> = (yield effects.call(channel)) as any;

    const editColumnOptionFieldsProps = {
        onSuccess: (displayColumns: string[], newColumnsAdded: boolean) => {
            updateColumnField(updateColumnFieldsChannel, displayColumns, newColumnsAdded);
        },
        existingColumnFields: columnOptionFields,
        allColumnFields: allColumnFields.map(field => ({
            referenceName: field.key,
            displayName: field.name
        }))
    };

    // open the column options panel
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/ColumnOptionsPanelProxy"],
            wrappedType: "column-options-panel-proxy",
            wrappedProps: editColumnOptionFieldsProps
        };
        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(updateColumnFieldsChannel)) as any;

    let providerContextToUpdate: ILibraryWorkItemDataProviderState = (yield effects.select(getWorkItemDataProviderStateFromState)) as any;

    // if new columns have been added in the panel, update them in the provider context and fetch updated data from data provider
    if (providerContextToUpdate && providerContextToUpdate.columnOptions != payload.displayColumns) {
        providerContextToUpdate.columnOptions = payload.displayColumns;
        providerContextToUpdate.continuationToken = undefined;

        yield effects.put(TestCasesActions.updateProviderState(providerContextToUpdate));

        const providerContextContract: Contracts.LibraryWorkItemsDataProviderRequest = (yield effects.call(
            getDataProviderContextContract,
            providerContextToUpdate
        )) as any;

        yield effects.put(TestCasesActions.clearTestCasesAndSetLoadingExperience());

        const data = (yield effects.call(getDataProviderContentAsync, pageContext, providerContextContract)) as any;

        const dataProviderState = (yield effects.call(createWorkItemDataProviderState, data, providerContextToUpdate.queryType)) as any;

        if (data) {
            yield effects.put(TestCasesActions.loadInitialTestCasesSucceeded(data.workItems, dataProviderState));
        }
    }

    // store the updated column options in the db through settings service
    const registryEntries = {
        TestCasesPivotColumnOptions: payload.displayColumns && payload.displayColumns.length > 0 ? payload.displayColumns.join(",") : ""
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
}

function updateColumnField(updateColumnFieldsChannel: Channel<any>, displayColumns: string[], newColumnsAdded: boolean) {
    updateColumnFieldsChannel.put({ displayColumns: displayColumns, newColumnsAdded: newColumnsAdded });
}
